import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useSuccessionTheme } from '../SuccessionThemeProvider';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import numeral from 'numeral';
import PartialSaleEditForm from './PartialSaleEditForm';
import { splitMyPartialSales } from './partialSalesPartition';
import { messagePartialSaleUser } from './partialSaleMessaging';
import { focusListingIdFromSearch } from './partialSalesDeepLink';
import { myPartialSalesStats } from './partialSalesStats';
import { SORT_OPTIONS, availableStates, applySortFilter } from './partialSalesSortFilter';
import { currencySymbol, formatCompactCurrency } from '../../../utils/formatters';
import { getAvatarStyle } from '../../../lib/s3';
import { hasAdditionalCardDetails } from './partialSaleCardDetails';
import PartialSaleAumMetric, { shouldShowPartialSaleAum } from './PartialSaleAumMetric';
import { partialSaleStatusDisplay } from './partialSaleStatus';
import { findMyInterest, eoiStatusKey, canMessageSeller } from './buyerEoiStatus';
import { openSuccessionDrawer } from '../../../actions/successionDrawerActionCreators';
import SingleColumnTemplate from '../../../../../ui/layouts/SingleColumnTemplate';
import Button from '../../../../../ui/canon/Button';
import Modal from '../../../../../ui/canon/Modal';
import TabGroup from '../../../../../ui/canon/TabGroup';
import {
  RectangleStackIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  UserGroupIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';

const EXPRESS_INTEREST_FOR_PARTIAL_SALE = gql`
  mutation expressInterestForPartialSale($partialSaleId: ID!, $requestMessage: String!) {
    expressInterestForPartialSale(partialSaleId: $partialSaleId, requestMessage: $requestMessage) {
      id
      segment_details
    }
  }
`;

const TOGGLE_PARTIAL_SALE_STATUS = gql`
  mutation TogglePartialSaleStatus($id: ID!, $sale_status: String!) {
    toggleStatusForPartialSale(id: $id, sale_status: $sale_status) {
      id
      sale_status
      reason_for_close
      listed_at
    }
  }
`;

const UPDATE_PARTIAL_SALE_INTEREST_STATUS = gql`
  mutation UpdatePartialSaleInterestStatus($partialSaleListingUserId: ID!, $status: String!) {
    updatePartialSaleInterestStatus(partialSaleListingUserId: $partialSaleListingUserId, status: $status) {
      id
      status
      buyer {
        id
        name
        allowed_name
      }
    }
  }
`;

const REMOVE_PARTIAL_SALE_LISTING = gql`
  mutation RemovePartialSaleListing($id: ID!, $userRole: String!) {
    removePartialSaleListing(id: $id, userRole: $userRole) {
      id
    }
  }
`;



const SuccessionPartialSalesMarketplace = ({ 
  partialSales, 
  user, 
  company, 
  partialSaleQuestions, 
  features,
  isModernizationEnabled,
  pageTitle,
  isMyPartialSales 
}) => {
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const dispatch = useDispatch();

  // Add CSS styles for 3D flip animation and primary pulse
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .transform-style-preserve-3d {
        transform-style: preserve-3d;
      }
      .backface-hidden {
        backface-visibility: hidden;
      }
      .rotate-y-180 {
        transform: rotateY(180deg);
      }
      @keyframes pulse-primary {
        0%, 100% {
          box-shadow: 0 0 0 2px var(--succession-primary), 0 0 10px var(--succession-primary), 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        50% {
          box-shadow: 0 0 0 2px var(--succession-primary), 0 0 25px var(--succession-primary), 0 0 40px var(--succession-primary), 0 4px 12px rgba(0, 0, 0, 0.1);
        }
      }
      /* Public marketplace masonry — real CSS (Tailwind columns-* utilities do not compile in
         this Succession surface). Cards pack vertically per column and can be any height, so
         expanding one card grows only its column. */
      .partial-sales-masonry {
        column-count: 1;
        column-gap: 1.5rem;
      }
      @media (min-width: 768px) {
        .partial-sales-masonry { column-count: 2; }
      }
      @media (min-width: 1024px) {
        .partial-sales-masonry { column-count: 3; }
      }
      .partial-sales-masonry > * {
        break-inside: avoid;
        -webkit-column-break-inside: avoid;
        margin-bottom: 1.5rem;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Use local state for listings so we can update them immediately
  const [localPartialSales, setLocalPartialSales] = useState(partialSales || []);
  
  // Update local state when prop changes
  React.useEffect(() => {
    setLocalPartialSales(partialSales || []);
  }, [partialSales]);

  const [expressInterest] = useMutation(EXPRESS_INTEREST_FOR_PARTIAL_SALE);
  const [toggleStatus] = useMutation(TOGGLE_PARTIAL_SALE_STATUS);
  const [updateInterestStatus] = useMutation(UPDATE_PARTIAL_SALE_INTEREST_STATUS);
  const [removeListing] = useMutation(REMOVE_PARTIAL_SALE_LISTING);
  const [selectedListing, setSelectedListing] = useState(null);
  const [isInterestModalOpen, setIsInterestModalOpen] = useState(false);
  const [interestMessage, setInterestMessage] = useState('');
  const [isSubmittingInterest, setIsSubmittingInterest] = useState(false);
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [editingListings, setEditingListings] = useState(new Map());
  // Per-card "More details" expand state. Ephemeral (not persisted), independent per card.
  const [expandedCards, setExpandedCards] = useState(new Set());
  // Listing the page should scroll to / highlight, from a deep-link email
  // (/succession/my_partial_sales?listing_id=<id>).
  const [focusedListingId, setFocusedListingId] = useState(null);
  
  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success', 'error', 'info'
  
  // Remove dialog state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [listingToRemove, setListingToRemove] = useState(null);

  // Public marketplace sort/filter (index only; gated to !isMyPartialSales below)
  const [sortValue, setSortValue] = useState('featured');
  const [selectedStates, setSelectedStates] = useState([]);
  const [isStateFilterOpen, setIsStateFilterOpen] = useState(false);

  // Company settings for seller avatar rendering (mirrors the search list view)
  const avatarCompanySettings = {
    avatar_directory: company?.avatar_directory,
    avatar_background_color: company?.avatar_background_color,
  };

  const partialSalesPropertyCasualty = company?.feature_types?.includes('partial_sales_property_casualty') || false;
  const hideProductSalesPurchasePrice = company?.feature_types?.includes('hide_partial_sales_purchase_price') || false;
  const partialSalesDetailedQuestions = company?.feature_types?.includes('partial_sales_detailed_questions') || false;

  // Toast notification function
  const showToastNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    
    // Auto-hide after 4 seconds
    setTimeout(() => {
      setShowToast(false);
    }, 4000);
  };

  const handleExpressInterest = (listing) => {
    setSelectedListing(listing);
    setInterestMessage(`Hi, I'm interested in learning more about your ${listing.segment_details || 'practice segment'}. Could we schedule a time to discuss the details?`);
    setIsInterestModalOpen(true);
  };

  const handlePublishListing = async (listing) => {
    try {
      const result = await toggleStatus({
        variables: {
          id: listing.id,
          sale_status: 'open'
        }
      });

      if (result.data?.toggleStatusForPartialSale) {
        // Update local state
        setLocalPartialSales(prev => 
          prev.map(item => 
            item.id === listing.id 
              ? { ...item, sale_status: 'open', listed_at: result.data.toggleStatusForPartialSale.listed_at }
              : item
          )
        );
      }
    } catch (error) {
      console.error('Error publishing listing:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleSubmitInterest = async () => {
    if (!selectedListing || !interestMessage.trim()) return;

    setIsSubmittingInterest(true);
    try {
      await expressInterest({
        variables: {
          partialSaleId: selectedListing.id,
          requestMessage: interestMessage.trim()
        }
      });
      
      setIsInterestModalOpen(false);
      setSelectedListing(null);
      setInterestMessage('');
      
      // Update local state to reflect that user has now expressed interest
      setLocalPartialSales(prev => 
        prev.map(listing => 
          listing.id === selectedListing.id 
            ? { 
                ...listing, 
                current_user_is_an_interest: true,
                interests_count: (listing.interests_count || 0) + 1
              }
            : listing
        )
      );
      
      // Show success message
      showToastNotification(intl.formatMessage({ id: 'succession.partial_sales.marketplace.toast_interest_success' }), 'success');
    } catch (error) {
      console.error('Error submitting interest:', error);

      // Check if it's a duplicate interest error
      if (error.message && error.message.includes('already expressed interest')) {
        showToastNotification(intl.formatMessage({ id: 'succession.partial_sales.marketplace.toast_interest_duplicate' }), 'error');
      } else {
        showToastNotification(intl.formatMessage({ id: 'succession.partial_sales.marketplace.toast_interest_failed' }), 'error');
      }
      
      // Update local state to reflect that user has already expressed interest
      if (error.message && error.message.includes('already expressed interest')) {
        setLocalPartialSales(prev => 
          prev.map(listing => 
            listing.id === selectedListing.id 
              ? { ...listing, current_user_is_an_interest: true }
              : listing
          )
        );
      }
    } finally {
      setIsSubmittingInterest(false);
    }
  };

  // Seller-only: set a buyer's EOI status via UpdateInterestStatus (owner-enforced server-side)
  // and optimistically reflect it on the card. Approve -> 'interested', Decline -> 'not_interested'.
  const applyInterestStatus = async (listingUserId, listingId, status, successKey, failureKey) => {
    try {
      const result = await updateInterestStatus({
        variables: {
          partialSaleListingUserId: listingUserId,
          status
        }
      });

      if (result.data?.updatePartialSaleInterestStatus) {
        // Update local state to reflect the status change
        setLocalPartialSales(prev =>
          prev.map(listing =>
            listing.id === listingId
              ? {
                  ...listing,
                  partial_sale_listing_users: listing.partial_sale_listing_users.map(listingUser =>
                    listingUser.id === listingUserId
                      ? { ...listingUser, status }
                      : listingUser
                  )
                }
              : listing
          )
        );

        showToastNotification(intl.formatMessage({ id: successKey }), 'success');
      }
    } catch (error) {
      console.error('Error updating interest status:', error);
      showToastNotification(intl.formatMessage({ id: failureKey }), 'error');
    }
  };

  const handleApproveInterest = (listingUserId, listingId) =>
    applyInterestStatus(
      listingUserId, listingId, 'interested',
      'succession.partial_sales.marketplace.toast_approve_success',
      'succession.partial_sales.marketplace.toast_approve_failed'
    );

  const handleDeclineInterest = (listingUserId, listingId) =>
    applyInterestStatus(
      listingUserId, listingId, 'not_interested',
      'succession.partial_sales.marketplace.toast_decline_success',
      'succession.partial_sales.marketplace.toast_decline_failed'
    );

  const handleRemoveClick = (listing) => {
    setListingToRemove(listing);
    setRemoveDialogOpen(true);
  };

  const handleRemoveConfirm = async () => {
    if (!listingToRemove) return;

    try {
      const userRole = listingToRemove.user.id === user.id ? 'seller' : 'buyer';
      
      await removeListing({
        variables: {
          id: listingToRemove.id,
          userRole: userRole
        }
      });

      // Remove from local state
      setLocalPartialSales(prev => prev.filter(listing => listing.id !== listingToRemove.id));
      
      showToastNotification(intl.formatMessage({ id: 'succession.partial_sales.marketplace.toast_remove_success' }), 'success');
    } catch (error) {
      console.error('Error removing listing:', error);
      if (error.message && error.message.includes('only remove closed listings')) {
        showToastNotification(intl.formatMessage({ id: 'succession.partial_sales.marketplace.toast_remove_only_closed' }), 'error');
      } else {
        showToastNotification(intl.formatMessage({ id: 'succession.partial_sales.marketplace.toast_remove_failed' }), 'error');
      }
    } finally {
      setRemoveDialogOpen(false);
      setListingToRemove(null);
    }
  };

  const handleRemoveCancel = () => {
    setRemoveDialogOpen(false);
    setListingToRemove(null);
  };



  const formatCurrency = (amount) => `${currencySymbol()}${numeral(amount || 0).format('0,0')}`;
  const formatNumber = (number) => numeral(number || 0).format('0,0');

  // Shared with the profile drawer's Partial Sale Listings via ./partialSaleStatus,
  // so the two cannot drift onto different status vocabularies again.
  const getStatusDisplay = (status) => {
    const { labelId, className } = partialSaleStatusDisplay(status);
    return { label: intl.formatMessage({ id: labelId }), color: className };
  };

  // --- Buyer EOI status (badge + Message Seller CTA) ---
  // Pure status logic lives in ./buyerEoiStatus (findMyInterest, eoiStatusKey, canMessageSeller).
  // Map a buyer-facing status to its i18n label + Succession token color.
  const getEoiStatusBadge = (status) => {
    const key = eoiStatusKey(status);
    const byKey = {
      approved: {
        label: intl.formatMessage({ id: 'succession.partial_sales.marketplace.eoi_status_approved' }),
        color: 'bg-succession-success-bg text-succession-success border border-succession-success',
      },
      declined: {
        label: intl.formatMessage({ id: 'succession.partial_sales.marketplace.eoi_status_declined' }),
        color: 'bg-succession-error-bg text-succession-error border border-succession-error',
      },
      pending: {
        label: intl.formatMessage({ id: 'succession.partial_sales.marketplace.eoi_status_pending' }),
        color: 'bg-succession-bg-secondary text-succession-text-secondary border border-succession-border',
      },
    };
    return { key, ...byKey[key] };
  };

  const getTotalRevenue = (listing) => {
    const feeBasedRevenue = parseFloat(listing.fee_based_revenue) || 0;
    const trailsRevenue = parseFloat(listing.trails_revenue) || 0;
    const directRevenue = parseFloat(listing.direct_revenue) || 0;
    const commissionRevenue = parseFloat(listing.commission_revenue) || 0;
    
    const total = feeBasedRevenue + trailsRevenue + directRevenue + commissionRevenue;
    return total;
  };

  const getRevenueMultiple = (listing) => {
    const totalRevenue = getTotalRevenue(listing);
    if (totalRevenue === 0) return 0;
    return (listing.asking_price || 0) / totalRevenue;
  };

  // Use local state for calculations and display
  const displayPartialSales = localPartialSales;

  // My Partial Sales splits into Owned / Interested. Hoisted out of the render so the
  // default tab can react to what the user actually has.
  const { owned, interested } = React.useMemo(
    () => (isMyPartialSales
      ? splitMyPartialSales(displayPartialSales, user.id)
      : { owned: [], interested: [] }),
    [isMyPartialSales, displayPartialSales, user.id]
  );

  // null = untouched, so the default keeps tracking the data until the user picks a tab.
  // Someone with no listings of their own lands on Interested rather than an empty view.
  const [pickedTab, setPickedTab] = useState(null);
  const activeListTab = pickedTab
    ?? (owned.length === 0 && interested.length > 0 ? 'interested' : 'owned');
  // Public index: sort/filter options + the visible (sorted/filtered) set the grid renders.
  // My Partial Sales is unaffected (passes through untouched).
  const stateOptions = React.useMemo(() => availableStates(localPartialSales), [localPartialSales]);
  const visiblePartialSales = React.useMemo(
    () => (isMyPartialSales
      ? displayPartialSales
      : applySortFilter(displayPartialSales, { sortValue, selectedStates })),
    [displayPartialSales, isMyPartialSales, sortValue, selectedStates]
  );

  // AUM sorts only make sense where the AUM column is actually rendered — the card metric is
  // gated to wealth tenants with AUM data (shouldShowPartialSaleAum), so the sort must match or
  // a non-wealth tenant gets "Sort by AUM" for a column that is not on screen.
  const sortOptions = React.useMemo(() => {
    const showsAum = (localPartialSales || []).some((l) => shouldShowPartialSaleAum(l, company));
    return showsAum ? SORT_OPTIONS : SORT_OPTIONS.filter((o) => o.field !== 'aum');
  }, [localPartialSales, company]);

  const toggleStateFilter = (state) => {
    setSelectedStates(prev => (
      prev.includes(state) ? prev.filter(s => s !== state) : [...prev, state]
    ));
  };

  const clearFilters = () => {
    setSortValue('featured');
    setSelectedStates([]);
  };

  // Deep-link from a partial-sale notification email
  // (/succession/my_partial_sales?listing_id=<id>): scroll to and briefly
  // highlight that listing — the one whose inquiry triggered the email.
  React.useEffect(() => {
    if (!isMyPartialSales) return undefined;
    const listingId = focusListingIdFromSearch(window.location.search);
    if (!listingId) return undefined;

    setFocusedListingId(listingId);
    const scrollTimer = setTimeout(() => {
      const el = document.getElementById(`partial-sale-card-${listingId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
    const clearTimer = setTimeout(() => setFocusedListingId(null), 4000);
    return () => { clearTimeout(scrollTimer); clearTimeout(clearTimer); };
  }, [isMyPartialSales]);

  const toggleExpanded = (listingId) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(listingId)) {
        next.delete(listingId);
      } else {
        next.add(listingId);
      }
      return next;
    });
  };

  const handleFlipCard = (listingId) => {
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(listingId)) {
        newSet.delete(listingId);
        setEditingListings(prev => {
          const newMap = new Map(prev);
          newMap.delete(listingId);
          return newMap;
        });
      } else {
        newSet.add(listingId);
        // Initialize the editing state with current listing data
        const listing = localPartialSales.find(l => l.id === listingId);
        if (listing) {
          setEditingListings(prev => new Map(prev).set(listingId, { ...listing }));
        }
      }
      return newSet;
    });
  };

  const handleEditSave = (listingId, updatedData) => {
    // Update the local state with the new data immediately
    setLocalPartialSales(prevListings => 
      prevListings.map(listing => 
        listing.id === listingId ? { ...listing, ...updatedData } : listing
      )
    );
    
    // Flip back to front of card
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      newSet.delete(listingId);
      return newSet;
    });
    
    setEditingListings(prev => {
      const newMap = new Map(prev);
      newMap.delete(listingId);
      return newMap;
    });
  };

  const handleEditCancel = (listingId) => {
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      newSet.delete(listingId);
      return newSet;
    });
    
    setEditingListings(prev => {
      const newMap = new Map(prev);
      newMap.delete(listingId);
      return newMap;
    });
  };



  const handleViewProfile = (userName) => {
    if (!userName) {
      console.error("Cannot open profile drawer: No username provided");
      return;
    }

    try {
      // Use Redux to open the succession drawer, same as ListCard component
      dispatch(openSuccessionDrawer(userName));
    } catch (error) {
      console.error("Error opening profile drawer:", error);
      // Fallback to URL navigation on error
      window.location.href = `/profiles/${userName}`;
    }
  };

  // Helper functions for page layout
  const getPageTitle = () => {
    return pageTitle || "Partial Sale Marketplace";
  };

  const getPageSubtitle = () => {
    if (isMyPartialSales) {
      return intl.formatMessage({ id: 'succession.partial_sales.marketplace.subtitle_my_sales' });
    }
    return intl.formatMessage({ id: 'succession.partial_sales.marketplace.subtitle_marketplace' });
  };

  const getActionButtons = () => {
    if (isMyPartialSales) {
      return (
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => window.location.href = "/succession/partial_sales"}
            variant="outline"
            size="md"
          >
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {intl.formatMessage({ id: 'succession.partial_sales.marketplace.back_to_marketplace' })}
          </Button>
          <Button
            onClick={() => window.location.href = "/succession/partial_sales/wizard"}
            variant="primary"
            size="md"
          >
            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {intl.formatMessage({ id: 'succession.partial_sales.marketplace.create_listing' })}
          </Button>
        </div>
      );
    } else {
      return (
        <Button
          onClick={() => window.location.href = "/succession/my_partial_sales"}
          variant="primary"
          size="md"
        >
          <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {intl.formatMessage({ id: 'succession.partial_sales.marketplace.my_partial_sales' })}
        </Button>
      );
    }
  };

  if (!localPartialSales || localPartialSales.length === 0) {
    return (
      <SingleColumnTemplate
        title={getPageTitle()}
        subtitle={getPageSubtitle()}
        actions={getActionButtons()}
        variant="wide"
      >
        <div className="text-center py-16">
          <div className="mx-auto w-16 h-16 bg-succession-bg-primary rounded-full flex items-center justify-center mb-4 border border-succession-border">
            <svg className="w-8 h-8 text-succession-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2 text-succession-text-primary transition-colors duration-300">
            {intl.formatMessage({ id: 'succession.partial_sales.marketplace.no_listings_title' })}
          </h2>
          <p className="text-lg mb-6 text-succession-text-secondary transition-colors duration-300">
            {intl.formatMessage({ id: 'succession.partial_sales.marketplace.no_listings_body' })}
          </p>
          <a
            href="/succession/partial_sales/wizard"
            className="inline-flex items-center px-6 py-3 bg-succession-primary text-white rounded-lg hover:opacity-80 transition-all"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {intl.formatMessage({ id: 'succession.partial_sales.marketplace.create_your_listing' })}
          </a>
        </div>
      </SingleColumnTemplate>
    );
  }

  return (
    <SingleColumnTemplate
      title={getPageTitle()}
      subtitle={getPageSubtitle()}
      actions={getActionButtons()}
      variant="wide"
    >
      {/* Stats - Only show on My Partial Sales page. Owned metrics count the
          current user's OWN listings only; buyer EOIs are reported separately so
          an expressed interest never inflates the Published count. */}
      {isMyPartialSales && (() => {
        const myStats = myPartialSalesStats(partialSales, user?.id);
        // Stat cards follow the Rolodex pattern (SuccessionRolodex.jsx): tinted surface,
        // accent icon, label over value — so the two "my stuff" pages read the same.
        // min-w-0 on the grid item: grid children default to min-width:auto, so a long
        // label ("Expressions of Interest") widens its track and pushes the row past the
        // container. Plain divs rather than dl/dt/dd — Succession pages are CSS-isolated,
        // so Tailwind preflight does not reliably zero the browser's default dd indent.
        const stat = (Icon, labelId, value) => (
          <div className="bg-succession-primary-10 overflow-hidden rounded-lg px-4 py-5 sm:p-6 min-w-0">
            <div className="flex items-center min-w-0">
              <Icon className="h-8 w-8 text-succession-primary flex-shrink-0" />
              <div className="ml-4 min-w-0 flex-1">
                <div className="text-sm font-medium text-succession-text-secondary truncate m-0">
                  {intl.formatMessage({ id: labelId })}
                </div>
                <div className="text-2xl font-semibold text-succession-text-primary m-0 truncate">
                  {value}
                </div>
              </div>
            </div>
          </div>
        );
        return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 mb-8">
          {stat(RectangleStackIcon, 'succession.partial_sales.marketplace.stats_total_listings', myStats.total)}
          {stat(CheckCircleIcon, 'succession.partial_sales.marketplace.stats_published', myStats.published)}
          {stat(PencilSquareIcon, 'succession.partial_sales.marketplace.stats_drafts', myStats.drafts)}
          {stat(UserGroupIcon, 'succession.partial_sales.marketplace.stats_total_clients', formatNumber(myStats.totalClients))}
          {stat(HeartIcon, 'succession.partial_sales.marketplace.stats_expressions_of_interest', myStats.eoiCount)}
        </div>
        );
      })()}

        {/* Sort + State filter controls (public marketplace index only) */}
        {!isMyPartialSales && (
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="flex items-center gap-2">
              <label htmlFor="partial-sales-sort" className="text-sm text-succession-text-secondary transition-colors duration-300">
                {intl.formatMessage({ id: 'succession.partial_sales.marketplace.sort_by_label' })}
              </label>
              <select
                id="partial-sales-sort"
                value={sortValue}
                onChange={(e) => setSortValue(e.target.value)}
                className="text-sm bg-succession-bg-primary border border-succession-border rounded-md px-3 py-2 text-succession-text-primary"
              >
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {intl.formatMessage({ id: `succession.partial_sales.marketplace.sort_${opt.value}` })}
                  </option>
                ))}
              </select>
            </div>

            {stateOptions.length > 0 && (
              <div className="relative">
                <div className="w-56">
                  <button
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={isStateFilterOpen}
                    onClick={() => setIsStateFilterOpen((o) => !o)}
                    className="w-full flex items-center justify-between text-sm bg-succession-bg-primary border border-succession-border rounded-md px-3 py-2 text-succession-text-primary"
                  >
                    <span>
                      {selectedStates.length === 0
                        ? intl.formatMessage({ id: 'succession.partial_sales.marketplace.filter_state_label' })
                        : intl.formatMessage({ id: 'succession.partial_sales.marketplace.filter_state_count' }, { count: selectedStates.length })}
                    </span>
                    <span aria-hidden="true" className="ml-2">▾</span>
                  </button>
                </div>
                {isStateFilterOpen && (
                  <div
                    role="listbox"
                    aria-multiselectable="true"
                    className="absolute z-10 mt-1 w-56 max-h-64 overflow-y-auto bg-succession-bg-primary border border-succession-border rounded-md shadow-lg p-2"
                  >
                    {stateOptions.map((state) => (
                      <label key={state} className="flex items-center gap-2 px-2 py-1 text-sm text-succession-text-primary cursor-pointer hover:bg-succession-bg-secondary rounded">
                        <input
                          type="checkbox"
                          checked={selectedStates.includes(state)}
                          onChange={() => toggleStateFilter(state)}
                        />
                        <span>{state}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedStates.map((state) => (
              <button
                key={state}
                type="button"
                onClick={() => toggleStateFilter(state)}
                aria-label={intl.formatMessage({ id: 'succession.partial_sales.marketplace.filter_remove_state' }, { state })}
                className="inline-flex items-center gap-1 text-xs bg-succession-bg-secondary text-succession-text-primary border border-succession-border rounded-full px-3 py-1"
              >
                <span>{state}</span>
                <span aria-hidden="true">×</span>
              </button>
            ))}

            {(selectedStates.length > 0 || sortValue !== 'featured') && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-succession-primary hover:underline"
              >
                {intl.formatMessage({ id: 'succession.partial_sales.marketplace.filter_clear_all' })}
              </button>
            )}

            <span className="sr-only" role="status" aria-live="polite">
              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.result_count' }, { count: visiblePartialSales.length })}
            </span>
          </div>
        )}

        {/* No-matches state: filters exclude everything, but listings do exist */}
        {!isMyPartialSales && visiblePartialSales.length === 0 && (
          <div className="text-center py-16">
            <h2 className="text-xl font-semibold mb-2 text-succession-text-primary transition-colors duration-300">
              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.no_matches_title' })}
            </h2>
            <p className="text-succession-text-secondary mb-6 transition-colors duration-300">
              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.no_matches_body' })}
            </p>
            <Button onClick={clearFilters} variant="primary" size="md">
              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.filter_clear_all' })}
            </Button>
          </div>
        )}

        {/* Listings — the public marketplace renders one grid; My Partial Sales
            splits into Owned ("My Listings") and Interested sections. */}
        {(() => {
          const renderListingCard = (listing) => {
            // Check if this listing has interests (only for My Partial Sales)
            const hasInterests = isMyPartialSales && listing.user.id === user.id && listing.partial_sale_listing_users && listing.partial_sale_listing_users.length > 0;
            
            // Check if there are pending interests that need approval
            const hasPendingInterests = hasInterests && listing.partial_sale_listing_users.some(listingUser => 
              !listingUser.status || listingUser.status === null || listingUser.status === 'expressed_interest'
            );

            return (
            <div
              key={listing.id}
              id={`partial-sale-card-${listing.id}`}
              className={`relative w-full ${
                listing.sale_status === 'new'
                  ? 'border-[var(--succession-border)] border-dashed' 
                  : hasInterests 
                    ? 'border-succession-primary border-2' 
                    : 'border-succession-border'
              }`}
              style={{ 
                perspective: '1000px',
                ...(hasInterests && {
                  boxShadow: hasPendingInterests 
                    ? '0 0 0 2px var(--succession-primary), 0 0 20px var(--succession-primary), 0 4px 12px rgba(0, 0, 0, 0.1)'
                    : '0 0 0 2px var(--succession-primary-bg), 0 4px 12px rgba(0, 0, 0, 0.1)'
                }),
                ...(hasPendingInterests && {
                  animation: 'pulse-primary 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }),
                // Deep-link highlight (wins over the interest shadow while focused)
                ...(focusedListingId && String(listing.id) === String(focusedListingId) && {
                  boxShadow: '0 0 0 3px var(--succession-primary), 0 6px 16px rgba(0, 0, 0, 0.15)'
                })
              }}
            >
              <div
                className={`relative w-full transition-transform duration-700 transform-style-preserve-3d ${
                  flippedCards.has(listing.id) ? 'rotate-y-180' : ''
                }`}
              >
                {/* Front of Card — in normal flow so it drives the card height (lets the card
                    grow inline on expand); the back edit form overlays it absolutely. The
                    inline minHeight gives collapsed cards a uniform baseline (CDN-safe vs an
                    arbitrary Tailwind class). */}
                <div
                  className="relative w-full backface-hidden bg-succession-bg-primary rounded-lg border shadow-sm hover:shadow-md transition-all duration-300"
                  style={{ minHeight: '28rem' }}
                >
                  <div className="p-6 flex flex-col">
                    <div>
                {/* Header — identity and status share one row so the practice name below
                {/* Header — identity and status share one row so the practice name below
                    is the card's single anchor, at full width. */}
                <div className="mb-4">
                  <div className="flex items-center justify-between gap-3 mb-2 min-h-[2rem]">
                    {/* Seller avatar + name (opens profile drawer). Your own listings get the
                        same treatment, marked "You" and non-interactive. */}
                    {listing.user && (() => {
                      const isOwnListing = listing.user.id === user.id;
                      const avatarStyle = getAvatarStyle(listing.user.profile_picture, avatarCompanySettings);
                      const sellerName = listing.user.allowed_name || listing.user.user_name;
                      const openSellerDrawer = (e) => {
                        e.stopPropagation();
                        handleViewProfile(listing.user.fb_user_name || listing.user.user_name);
                      };
                      const Identity = isOwnListing ? 'div' : 'button';
                      const identityProps = isOwnListing
                        ? {}
                        : {
                            type: 'button',
                            onClick: openSellerDrawer,
                            'aria-label': intl.formatMessage({ id: 'succession.partial_sales.marketplace.view_seller_profile' }, { name: sellerName }),
                          };
                      return (
                        <Identity
                          {...identityProps}
                          className="flex items-center gap-2 text-left min-w-0"
                        >
                          {listing.user.profile_picture ? (
                            <span
                              className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"
                              style={{ backgroundColor: avatarStyle.backgroundColor }}
                            >
                              <img
                                src={avatarStyle.url}
                                alt=""
                                className="w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                                style={avatarStyle.isNewStyle ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
                              />
                            </span>
                          ) : (
                            <span
                              aria-hidden="true"
                              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-succession-primary-10 text-succession-primary text-sm font-semibold"
                            >
                              {(sellerName || '?').charAt(0).toUpperCase()}
                            </span>
                          )}
                          <span className={`text-sm font-medium truncate ${
                            isOwnListing
                              ? 'text-succession-text-secondary'
                              : 'text-succession-primary hover:text-succession-primary-hover'
                          }`}>
                            {sellerName}
                          </span>
                          {isOwnListing && (
                            <span className="flex-shrink-0 px-1.5 py-0.5 text-[11px] font-medium rounded bg-succession-bg-secondary text-succession-text-secondary border border-succession-border">
                              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.your_listing_badge' })}
                            </span>
                          )}
                        </Identity>
                      );
                    })()}
                    {/* Status — My Partial Sales only. The public marketplace is scoped to
                        sale_status: :open server-side, so every card there is Published and the
                        badge carries no information. Here it does: owned listings can be Draft or
                        Closed, and a listing you expressed interest in can be Closed by its seller. */}
                    {isMyPartialSales && (
                      <div className={`flex-shrink-0 inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusDisplay(listing.sale_status).color}`}>
                        {getStatusDisplay(listing.sale_status).label}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1 text-succession-text-primary transition-colors duration-300">
                      {listing.segment_details || 'Practice Segment'}
                    </h3>
                    <div className="flex items-start text-sm text-succession-text-secondary transition-colors duration-300">
                      <svg className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="break-words">
                        {listing.states?.join(', ') || 'Location not specified'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Views and Interests - Only show on My Partial Sales */}
                {isMyPartialSales && (
                  <div className="mb-4 space-y-2">
                    <div className="flex justify-between items-center px-3 py-2 bg-succession-bg-secondary rounded-lg transition-colors duration-300">
                      <div className="flex items-center text-sm text-succession-text-secondary">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span className="font-medium text-succession-text-primary">{listing.views_count || 0}</span>
                        <span className="ml-1">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.views' })}</span>
                      </div>
                      <div className="flex items-center text-sm text-succession-text-secondary">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                        <span className="font-medium text-succession-text-primary">{listing.interests_count || 0}</span>
                        <span className="ml-1">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.interests' })}</span>
                      </div>
                    </div>
                    {(listing.listed_at || listing.created_at) && (
                      <div className="flex items-center text-sm text-succession-text-secondary px-3 py-2 bg-succession-bg-secondary rounded-lg transition-colors duration-300">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="mr-1">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.listed_label' })}</span>
                        <span className="font-medium text-succession-text-primary">
                          {listing.listed_at ? new Date(listing.listed_at).toLocaleDateString() : new Date(listing.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Key Metrics — clients, revenue, and (for wealth tenants with AUM data) the
                    listing's scoped AUM share one row; asking price sits on its own line below. */}
                <div className={`grid ${shouldShowPartialSaleAum(listing, company) ? 'grid-cols-3' : 'grid-cols-2'} gap-4 mb-4`}>
                  <div>
                    <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.clients_label' })}</div>
                    <div className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">
                      {formatNumber(listing.number_of_clients)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.revenue_label' })}</div>
                    <div className="text-lg font-semibold text-succession-success">
                      {formatCompactCurrency(getTotalRevenue(listing))}
                    </div>
                  </div>
                  {/* AUM — scoped to THIS listing, never the seller's platform total. */}
                  <PartialSaleAumMetric listing={listing} company={company} />
                </div>
                {!hideProductSalesPurchasePrice && (
                  <div className="mb-4">
                    <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.asking_price_label' })}</div>
                    <div className="text-lg font-semibold text-succession-primary">
                      {(() => {
                        return !listing.asking_price || listing.asking_price === 0 || listing.asking_price === '0' || parseFloat(listing.asking_price) === 0 ? intl.formatMessage({ id: 'succession.partial_sales.marketplace.negotiable' }) : formatCurrency(listing.asking_price);
                      })()}
                    </div>
                  </div>
                )}

                {/* More details toggle — expands the card inline (no inner scroll). Only shown
                    when there is additional content to reveal. */}
                {hasAdditionalCardDetails(listing, { detailedQuestions: partialSalesDetailedQuestions }) && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleExpanded(listing.id); }}
                    aria-expanded={expandedCards.has(listing.id)}
                    aria-controls={`card-details-${listing.id}`}
                    className="mb-4 self-start text-sm font-medium text-succession-primary hover:text-succession-primary-hover"
                  >
                    {intl.formatMessage({ id: expandedCards.has(listing.id)
                      ? 'succession.partial_sales.marketplace.details_show_less'
                      : 'succession.partial_sales.marketplace.details_show_more' })}
                  </button>
                )}

                {expandedCards.has(listing.id) && (
                  <div id={`card-details-${listing.id}`}>
                {/* Description */}
                {listing.description && (
                  <div className="mb-4">
                    <p className="text-sm text-succession-text-secondary transition-colors duration-300 leading-relaxed">
                      {listing.description}
                    </p>
                  </div>
                )}

                {/* Revenue Breakdown */}
                <div className="mb-4">
                  <div className="text-sm font-medium mb-2 text-succession-text-primary transition-colors duration-300">
                    {intl.formatMessage({ id: 'succession.partial_sales.marketplace.revenue_breakdown_label' })}
                  </div>
                  <div className="space-y-1 text-xs">
                    {listing.fee_based_revenue > 0 && (
                      <div className="flex justify-between">
                        <span className="text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.fee_based' })}</span>
                        <span className="text-succession-text-primary transition-colors duration-300">{formatCurrency(listing.fee_based_revenue)}</span>
                      </div>
                    )}
                    {listing.trails_revenue > 0 && (
                      <div className="flex justify-between">
                        <span className="text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.trails' })}</span>
                        <span className="text-succession-text-primary transition-colors duration-300">{formatCurrency(listing.trails_revenue)}</span>
                      </div>
                    )}
                    {listing.direct_revenue > 0 && (
                      <div className="flex justify-between">
                        <span className="text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.direct' })}</span>
                        <span className="text-succession-text-primary transition-colors duration-300">{formatCurrency(listing.direct_revenue)}</span>
                      </div>
                    )}
                    {listing.commission_revenue > 0 && (
                      <div className="flex justify-between">
                        <span className="text-succession-text-secondary transition-colors duration-300">
                          {partialSalesPropertyCasualty ? intl.formatMessage({ id: 'succession.partial_sales.marketplace.property_casualty' }) : intl.formatMessage({ id: 'succession.partial_sales.marketplace.commission' })}
                        </span>
                        <span className="text-succession-text-primary transition-colors duration-300">{formatCurrency(listing.commission_revenue)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* More Details */}
                {partialSalesDetailedQuestions && (listing.client_demographics_details || listing.services_provided_details || listing.other_details) && (
                  <div>
                    <h4 className="font-medium text-succession-text-primary mb-2 transition-colors duration-300">
                      {intl.formatMessage({ id: 'succession.partial_sales.marketplace.more_details' })}
                    </h4>
                    <div className="bg-succession-bg-secondary rounded-lg p-3 text-sm transition-colors duration-300 space-y-2">
                      {listing.client_demographics_details && (
                        <div>
                          <span className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.client_demographics_label' })}</span>
                          <p className="text-succession-text-primary">{listing.client_demographics_details}</p>
                        </div>
                      )}
                      {listing.services_provided_details && (
                        <div>
                          <span className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.services_provided_label' })}</span>
                          <p className="text-succession-text-primary">{listing.services_provided_details}</p>
                        </div>
                      )}
                      {listing.other_details && (
                        <div>
                          <span className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.other_label' })}</span>
                          <p className="text-succession-text-primary">{listing.other_details}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                  </div>
                )}

                {/* Interests - Only show on My Partial Sales for own listings with interests */}
                {isMyPartialSales && listing.user.id === user.id && listing.partial_sale_listing_users && listing.partial_sale_listing_users.length > 0 && (
                  <div>
                    <h4 className="font-medium text-succession-text-primary mb-2 transition-colors duration-300 flex items-center">
                      {intl.formatMessage({ id: 'succession.partial_sales.marketplace.interests_section' }, { count: listing.partial_sale_listing_users.length })}
                      {hasPendingInterests && (
                        <span className="ml-2 flex items-center">
                          <span className="relative flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-succession-primary opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-succession-primary"></span>
                          </span>
                          <span className="ml-1 text-xs text-succession-primary font-medium">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.new_badge' })}</span>
                        </span>
                      )}
                    </h4>
                    <div className="bg-succession-bg-secondary rounded-lg p-3 text-sm max-h-48 overflow-y-auto transition-colors duration-300 space-y-3">
                      {listing.partial_sale_listing_users.map((listingUser, index) => {
                        const isPending = !listingUser.status || listingUser.status === null || listingUser.status === 'expressed_interest';
                        return (
                        <div key={listingUser.id || index} className={`flex items-center justify-between p-2 bg-succession-bg-primary rounded border transition-all duration-300 ${
                          isPending ? 'border-succession-primary bg-succession-primary-bg' : 'border-succession-border'
                        }`}>
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <div>
                                <Button
                                  as="button"
                                  onClick={() => handleViewProfile(listingUser.user.fb_user_name || listingUser.user.user_name)}
                                  variant="ghost"
                                  size="sm"
                                  className="font-medium text-succession-primary hover:text-succession-primary-hover underline p-0 h-auto"
                                >
                                  {listingUser.user.allowed_name || listingUser.user.name || 'Anonymous'}
                                </Button>  
                                <p className="text-xs text-succession-text-secondary">
                                  {listingUser.user.city && listingUser.user.province ? 
                                    `${listingUser.user.city}, ${listingUser.user.province}` : 
                                    'Location not specified'
                                  }
                                </p>
                                {listingUser.user.assets_under_management && (
                                  <p className="text-xs text-succession-text-secondary">
                                    {currencySymbol()}{numeral(listingUser.user.assets_under_management).format('0,0')} AUM
                                  </p>
                                )}
                              </div>
                            </div>
                            {listingUser.request_message && (
                              <div className="mt-2 p-2 bg-succession-bg-secondary rounded text-xs">
                                <span className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_message_label' })}</span>
                                <p className="text-succession-text-primary mt-1">{listingUser.request_message}</p>
                              </div>
                            )}
                          </div>
                          <div className="ml-3">
                            {/* Pending: approve or decline. Approved: message. Declined: badge. */}
                            {(!listingUser.status || listingUser.status === null || listingUser.status === 'expressed_interest') ? (
                              <div className="flex items-center space-x-2">
                                <Button
                                  onClick={() => handleApproveInterest(listingUser.id, listing.id)}
                                  variant="primary"
                                  size="sm"
                                  className="bg-succession-success hover:bg-succession-success/90 text-xs"
                                >
                                  {intl.formatMessage({ id: 'succession.partial_sales.marketplace.approve_button' })}
                                </Button>
                                <Button
                                  onClick={() => handleDeclineInterest(listingUser.id, listing.id)}
                                  variant="outline"
                                  size="sm"
                                  className="text-succession-error border-succession-error hover:bg-succession-error-bg text-xs"
                                >
                                  {intl.formatMessage({ id: 'succession.partial_sales.marketplace.decline_button' })}
                                </Button>
                              </div>
                            ) : listingUser.status === 'interested' ? (
                              <Button
                                onClick={() => {
                                // Create and submit form dynamically
                                const form = document.createElement('form');
                                form.method = 'POST';
                                form.action = '/messages/create_chat_from_partial_sale_user';
                                
                                // Add friendly_id
                                const friendlyIdInput = document.createElement('input');
                                friendlyIdInput.type = 'hidden';
                                friendlyIdInput.name = 'friendly_id';
                                friendlyIdInput.value = listingUser.friendly_name;
                                form.appendChild(friendlyIdInput);
                                
                                // Add authenticity token
                                const tokenInput = document.createElement('input');
                                tokenInput.type = 'hidden';
                                tokenInput.name = 'authenticity_token';
                                tokenInput.value = window.ReactOnRails?.authenticityToken() || '';
                                form.appendChild(tokenInput);
                                
                                // Add to document and submit
                                document.body.appendChild(form);
                                form.submit();
                                document.body.removeChild(form);
                              }}
                              variant="primary"
                              size="sm"
                              className="text-xs"
                            >
                              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.message_button' })}
                            </Button>
                            ) : (
                              <span className="px-3 py-1 bg-succession-error-bg text-succession-error border border-succession-error text-xs rounded">
                                {intl.formatMessage({ id: 'succession.partial_sales.marketplace.eoi_status_declined' })}
                              </span>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

                    {/* Action Button - Always at bottom */}
                    <div className="mt-4 pt-4 border-t border-succession-border">
                    {listing.user.id === user.id ? (
                      // Own listing - show edit/manage actions
                      <>
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => handleFlipCard(listing.id)}
                            variant="primary"
                            size="md"
                            className="flex-1"
                          >
                            {intl.formatMessage({ id: 'succession.partial_sales.marketplace.edit_button' })}
                          </Button>
                          {listing.sale_status === 'new' && (
                            <Button
                              onClick={() => handlePublishListing(listing)}
                              variant="primary"
                              size="md"
                              className="flex-1 bg-succession-success hover:bg-succession-success/90"
                            >
                              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.publish_button' })}
                            </Button>
                          )}
                        </div>
                        <Button
                          onClick={() => handleRemoveClick(listing)}
                          variant="primary"
                          size="md"
                          className="w-full mt-2 bg-succession-error hover:opacity-90"
                        >
                          {intl.formatMessage({ id: 'succession.partial_sales.marketplace.remove_button' })}
                        </Button>
                      </>
                    ) : listing.current_user_is_an_interest ? (
                      // Buyer has expressed interest - show EOI status badge (+ Message Seller when
                      // approved). Placed before the draft/closed checks so a buyer always sees their
                      // own standing regardless of later sale_status transitions.
                      (() => {
                        const myInterest = findMyInterest(listing, user.id);
                        const badge = getEoiStatusBadge(myInterest && myInterest.status);
                        const isClosed = listing.sale_status === 'closed';
                        const showMessageSeller = canMessageSeller(badge.key, listing.sale_status);
                        return (
                          <div className="w-full space-y-2">
                            <div className="flex items-center justify-center">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                                {badge.label}
                              </span>
                            </div>
                            {showMessageSeller ? (
                              <Button
                                onClick={() => messagePartialSaleUser(myInterest.friendly_name)}
                                variant="primary"
                                size="md"
                                className="w-full"
                              >
                                {intl.formatMessage({ id: 'succession.partial_sales.marketplace.message_seller_button' })}
                              </Button>
                            ) : isClosed ? (
                              <p className="text-xs text-center text-succession-text-secondary">
                                {intl.formatMessage({ id: 'succession.partial_sales.marketplace.listing_closed_note' })}
                              </p>
                            ) : (
                              <Button
                                disabled
                                variant="outline"
                                size="md"
                                className="w-full"
                              >
                                {intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_already_expressed' })}
                              </Button>
                            )}
                          </div>
                        );
                      })()
                    ) : listing.sale_status === 'new' ? (
                      // Draft listing from another user - disabled
                      <Button
                        disabled
                        variant="outline"
                        size="md"
                        className="w-full"
                      >
                        {intl.formatMessage({ id: 'succession.partial_sales.marketplace.draft_not_available' })}
                      </Button>
                    ) : listing.sale_status === 'closed' ? (
                      // Closed listing from another user - allow buyer to remove
                      <Button
                        onClick={() => handleRemoveClick(listing)}
                        variant="primary"
                        size="md"
                        className="w-full bg-succession-error hover:opacity-90"
                      >
                        Remove
                      </Button>
                    ) : (
                      // Published listing from another user - express interest
                      <Button
                        onClick={() => handleExpressInterest(listing)}
                        variant="primary"
                        size="md"
                        className="w-full"
                      >
                        {intl.formatMessage({ id: 'succession.partial_sales.marketplace.express_interest' })}
                      </Button>
                    )}
                    </div>
                  </div>
                </div>

                {/* Back of Card - Edit Form */}
                <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-succession-bg-primary rounded-lg border shadow-sm overflow-hidden">
                  <div className="h-full overflow-y-auto">
                    {flippedCards.has(listing.id) && editingListings.has(listing.id) && (
                      <PartialSaleEditForm
                        listing={editingListings.get(listing.id)}
                        onCancel={() => handleEditCancel(listing.id)}
                        onSave={(updatedData) => handleEditSave(listing.id, updatedData)}
                        company={company}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            );
          };

          const emptyNote = (bodyId) => (
            <p className="text-sm text-succession-text-secondary mb-4">
              {intl.formatMessage({ id: bodyId })}
            </p>
          );

          if (!isMyPartialSales) {
            // CSS multi-column masonry: cards pack vertically per column, so expanding one card
            // grows only its column instead of stretching a whole grid row. Cards carry
            // break-inside-avoid + mb-6 (see renderListingCard).
            return (
              <div className="partial-sales-masonry">
                {visiblePartialSales.map(renderListingCard)}
              </div>
            );
          }

          // Tabs rather than stacked sections: the two lists are alternatives, and stacking
          // them meant scrolling past every owned listing to reach the interested ones.
          const ownedLabel = intl.formatMessage({ id: 'succession.partial_sales.marketplace.section_owned' });
          const interestedLabel = intl.formatMessage({ id: 'succession.partial_sales.marketplace.section_interested' });
          const isOwnedTab = activeListTab === 'owned';
          const activeList = isOwnedTab ? owned : interested;

          return (
            <div>
              <TabGroup
                tabs={[
                  { key: 'owned', label: ownedLabel, count: owned.length },
                  { key: 'interested', label: interestedLabel, count: interested.length },
                ]}
                activeTab={activeListTab}
                onChange={setPickedTab}
                variant="solid"
                className="mb-6"
              />
              <div role="tabpanel" aria-label={isOwnedTab ? ownedLabel : interestedLabel}>
                {activeList.length > 0
                  ? <div className="space-y-6">{activeList.map(renderListingCard)}</div>
                  : emptyNote(isOwnedTab
                      ? 'succession.partial_sales.marketplace.section_owned_empty'
                      : 'succession.partial_sales.marketplace.section_interested_empty')}
              </div>
            </div>
          );
        })()}

        {/* Interest Modal */}
        <Modal
          open={isInterestModalOpen && !!selectedListing}
          onClose={() => {
            setIsInterestModalOpen(false);
            setSelectedListing(null);
            setInterestMessage('');
          }}
          // Dim the marketplace behind the modal. Inline rgba (not the canon default
          // bg-black/40) because that Tailwind opacity utility does not paint on this
          // Succession surface — same class-availability gap as the masonry columns-*.
          // The canon Modal now portals to document.body, so its z-50 overlay sits above the
          // sticky Succession header (z-40) and dims the whole page.
          overlayStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
        >
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4 text-succession-text-primary transition-colors duration-300">
              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_modal_title' })}
            </h2>
            <p className="text-sm mb-4 text-succession-text-secondary transition-colors duration-300">
              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_modal_desc' }, { segment: selectedListing?.segment_details || '' })}
            </p>
            <textarea
              value={interestMessage}
              onChange={(e) => setInterestMessage(e.target.value)}
              className="w-full px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] focus:border-transparent bg-succession-bg-secondary text-succession-text-primary transition-colors duration-300"
              rows={4}
              placeholder={intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_modal_placeholder' })}
            />
            <div className="flex justify-end space-x-3 mt-4">
              <Button
                onClick={() => {
                  setIsInterestModalOpen(false);
                  setSelectedListing(null);
                  setInterestMessage('');
                }}
                variant="outline"
                size="md"
              >
                {intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_modal_cancel' })}
              </Button>
              <Button
                onClick={handleSubmitInterest}
                disabled={isSubmittingInterest || !interestMessage.trim()}
                variant="primary"
                size="md"
              >
                {isSubmittingInterest ? (
                  <div className="flex items-center">
                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" className="opacity-25" />
                      <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_modal_sending' })}
                  </div>
                ) : (
                  intl.formatMessage({ id: 'succession.partial_sales.marketplace.interest_modal_send' })
                )}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Toast Notification */}
        {showToast && (
          <div className={`fixed top-4 right-4 z-50 max-w-sm w-full transform transition-all duration-300 ease-in-out ${
            showToast ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
          }`}>
            <div className={`rounded-lg p-4 shadow-lg border-l-4 ${
              toastType === 'success' 
                ? 'bg-succession-success-bg border-succession-success text-succession-success' 
                : toastType === 'error'
                ? 'bg-succession-error-bg border-succession-error text-succession-error'
                : 'bg-succession-primary-20 border-succession-primary text-succession-primary'
            }`}>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  {toastType === 'success' ? (
                    <svg className="w-5 h-5 text-succession-success" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : toastType === 'error' ? (
                    <svg className="w-5 h-5 text-succession-error" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-succession-primary" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium">{toastMessage}</p>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <Button
                    as="button"
                    onClick={() => setShowToast(false)}
                    variant="ghost"
                    size="sm"
                    className={`p-0 h-auto ${
                      toastType === 'success' 
                        ? 'text-succession-success hover:opacity-80' 
                        : toastType === 'error'
                        ? 'text-succession-error hover:opacity-80'
                        : 'text-succession-primary hover:opacity-80'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Remove Confirmation Dialog */}
        <Modal
          open={removeDialogOpen}
          onClose={handleRemoveCancel}
          className="bg-succession-bg-primary text-succession-text-primary rounded-lg shadow-succession-lg border border-succession-border w-full max-w-md"
        >
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4 text-succession-text-primary transition-colors duration-300">
              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.remove_modal_title' })}
            </h2>
            <p className="text-sm mb-6 text-succession-text-secondary transition-colors duration-300">
              {intl.formatMessage({ id: 'succession.partial_sales.marketplace.remove_modal_desc' })}
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                onClick={handleRemoveCancel}
                variant="outline"
                size="md"
              >
                {intl.formatMessage({ id: 'succession.partial_sales.marketplace.remove_modal_cancel' })}
              </Button>
              <Button
                onClick={handleRemoveConfirm}
                variant="primary"
                size="md"
                className="bg-succession-error hover:opacity-90"
              >
                {intl.formatMessage({ id: 'succession.partial_sales.marketplace.remove_modal_confirm' })}
              </Button>
            </div>
          </div>
        </Modal>
    </SingleColumnTemplate>
  );
};

export default SuccessionPartialSalesMarketplace;